import { Controller } from "@hotwired/stimulus"

// Off by default, and sticky per dashboard across visits once turned on: the chosen interval is
// stored in localStorage keyed by dashboard id, a per-browser UI preference, not something that
// needs a database column or to sync across devices. Mirrors live_chart_controller.js's own
// polling idiom (setInterval/clearInterval, skip a tick while the tab is hidden, a dropped fetch
// just leaves the last-known content showing) but as its own controller: the poll target here is
// a whole GridStack grid, not a single chart div, and dashboard_grid_controller.js already owns
// every .grid-stack-item's DOM (its gs-x/y/w/h attributes and live drag/resize state), so a poll
// response is parsed into a detached DOM and only each matching [data-widget-id]'s own .card-body
// innerHTML is copied over, never the grid-stack-item wrapper itself, which would fight GridStack
// for control of the exact same elements.
const INTERVALS_MS = { "30s": 30_000, "1m": 60_000, "5m": 300_000 }

export default class extends Controller {
  static values = { url: String, dashboardId: String }
  static targets = ["select"]

  connect() {
    this.selectTarget.value = this.storedInterval()
    this.schedulePoll()
  }

  disconnect() {
    this.stopPoll()
  }

  intervalChanged() {
    this.storeInterval(this.selectTarget.value)
    this.schedulePoll()
  }

  schedulePoll() {
    this.stopPoll()
    const interval = INTERVALS_MS[this.selectTarget.value]
    if (!interval) return
    this.timer = setInterval(() => this.refresh(), interval)
  }

  stopPoll() {
    if (this.timer) clearInterval(this.timer)
  }

  async refresh() {
    if (document.hidden) return

    try {
      const response = await fetch(this.urlValue, { headers: { Accept: "text/html" } })
      if (!response.ok) return

      const template = document.createElement("template")
      template.innerHTML = await response.text()

      template.content.querySelectorAll("[data-widget-id]").forEach((updated) => {
        const current = this.element.querySelector(`[data-widget-id="${updated.dataset.widgetId}"] .card-body`)
        const updatedBody = updated.querySelector(".card-body")
        if (current && updatedBody) current.innerHTML = updatedBody.innerHTML
      })
    } catch (error) {
      // A dropped poll leaves the last-known numbers on screen; not worth interrupting anyone
      // over what's still just a periodic refresh; the next scheduled tick tries again. Logged,
      // not silent, the same reasoning dashboard_widget_builder_controller.js's own fetchPreview()
      // already documents for exactly this kind of failure.
      console.error("Dashboard refresh request failed:", error)
    }
  }

  // localStorage can throw in some browsers/private-browsing modes; read and write both guarded
  // so a blocked/unavailable store just falls back to "off" rather than breaking the page.
  storedInterval() {
    try {
      return localStorage.getItem(this.storageKey()) || "off"
    } catch {
      return "off"
    }
  }

  storeInterval(value) {
    try {
      localStorage.setItem(this.storageKey(), value)
    } catch {
      // Not persisted this time; the dropdown itself still reflects the choice for the rest of
      // this page view, it just won't survive a reload.
    }
  }

  storageKey() {
    return `dashboard-${this.dashboardIdValue}-refresh-interval`
  }
}
